import { forwardRef } from 'react';
import { ChevronRight } from 'react-bootstrap-icons';

export interface CallToActionCardBaseProps {
  linkText: string;
  linkSubtext?: string;
  className?: string;
}
export interface CallToActionCardWithClickProps
  extends CallToActionCardBaseProps {
  onClick: () => void;
  externalHref?: never;
}
export interface CallToActionCardWithHrefProps
  extends CallToActionCardBaseProps {
  externalHref: string;
  onClick?: never;
}

export type CallToActionCardProps =
  | CallToActionCardWithClickProps
  | CallToActionCardWithHrefProps;

export const CallToActionCard = forwardRef<
  HTMLDivElement,
  CallToActionCardProps
>((props, ref) => {
  const { linkText, linkSubtext, className } = props;
  const containsExternalHref = props.externalHref !== undefined;

  const handleClick = () => {
    if (containsExternalHref) {
      window.location.assign(props.externalHref);
    } else {
      props.onClick && props.onClick();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  return (
    <button
      className="bg-background-paper border-l-sm border-action-button pl-inset outline-action-button relative min-h-[5rem] w-full cursor-pointer rounded p-3 shadow outline-2 focus-within:outline sm:w-[25rem]"
      type="button"
      aria-label={`${linkText}, ${linkSubtext}. Press Enter or Space to select.`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <div
        ref={ref}
        role="link"
        data-testid={`card-${linkText.toLowerCase().replace(/\s+/g, '-')}`}
        className={`${className} text-bodyLg flex flex-1 cursor-pointer items-center justify-between gap-2`}
      >
        <div className="flex flex-1 flex-col text-left">
          <h2 className="text-bodyLg text-action-link underline underline-offset-2 hover:decoration-auto">
            {linkText}
          </h2>
          <p className="text-text-body text-body mt-1">{linkSubtext}</p>
        </div>
        <ChevronRight size={16} className="fill-action-link" />
      </div>
    </button>
  );
});

CallToActionCard.displayName = 'CallToActionCard';
